<template>
  <div class="finalist-grid">
    <!-- Alternative Sites Grid -->
    <themed-grid
      v-if="hasComps"
      :data="compData"
      :fields="gridFields"
      :columns="3"
      :pagination="false"
      :clickable="true"
      :loading="isLoading"
      :loading-message="loadingMessage"
      :empty-message="emptyMessage"
      empty-icon="home"
      @card-click="navigateToComp"
    >
      <template #actions="{ item }">
        <themed-button
          variant="ghost"
          size="sm"
          @click.stop="toggleFavorite(item)"
          :class="{ 'finalist-grid__card-action-button--active': item.pivot && item.pivot.is_favorite }"
          :aria-label="(item.pivot && item.pivot.is_favorite) ? 'Remove from favorites' : 'Add to favorites'"
        >
          <theme-icon
            :icon="(item.pivot && item.pivot.is_favorite) ? 'heart-filled' : 'heart-outline'"
            type="icons"
            classes="w-4 h-4"
          />
        </themed-button>

        <themed-button
          v-if="showRemoveButton"
          variant="ghost"
          size="sm"
          @click.stop="removeComp(item)"
          aria-label="Remove comp from consideration"
        >
          <theme-icon icon="x" type="icons" classes="w-4 h-4" />
        </themed-button>
      </template>
    </themed-grid>

    <!-- Criteria Comparison Table -->
    <themed-table
      v-if="showCriteriaComparison"
      :data="ksdCriteria"
      :pagination="false"
      :hoverable="true"
      :bordered="true"
      aria-label="Comp criteria comparison"
    >
      <!-- Custom header with comp links -->
      <template #header>
        <tr>
          <th class="themed-table-header-cell" style="text-align: left;">
            Criteria
          </th>
          <th
            v-for="comp in compData"
            :key="'header-' + comp.id"
            class="themed-table-header-cell"
            style="text-align: center;"
          >
            <a :href="getCompUrl(comp)" class="finalist-grid__comparison-comp-link">
              <p class="finalist-grid__comparison-comp-name">{{ comp.address_line_1 }}</p>
              <p class="finalist-grid__comparison-comp-location">{{ comp.city }}, {{ comp.state }}</p>
            </a>
          </th>
        </tr>
      </template>

      <!-- Custom body with criteria and star ratings -->
      <template #default>
        <tr
          v-for="criterion in ksdCriteria"
          :key="'criterion-' + criterion.id"
        >
          <th scope="row" class="themed-table-cell" style="text-align: left; font-weight: 500;">
            {{ criterion.driver }}
          </th>
          <td
            v-for="comp in compData"
            :key="'score-' + criterion.id + '-' + comp.id"
            class="themed-table-cell"
            style="text-align: center;"
          >
            <div class="finalist-grid__comparison-stars" :aria-label="getStarLabel(comp, criterion)">
              <span 
                v-for="star in 5" 
                :key="'star-' + star" 
                :class="star <= getCompKSDScore(criterion, comp.pivot.id) ? 'star-filled' : 'star-empty'"
              >
                <theme-icon
                  icon="star-filled"
                  type="icons"
                  classes="w-5 h-5"
                />
              </span>
            </div>
          </td>
        </tr>
      </template>
    </themed-table>
  </div>
</template>

<script>
import moment from 'moment';
import ThemeIcon from '../ThemeIcon.vue';
import ThemedGrid from '../ThemedGrid.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemedTable from '../ThemedTable.vue';

export default {
  name: 'FinalistGrid',

  components: {
    ThemeIcon,
    ThemedGrid,
    ThemedButton,
    ThemedTable
  },

  props: {
    compData: {
      type: Array,
      required: true,
      default: () => []
    },
    ksdCriteria: {
      type: Array,
      default: () => []
    },
    projectId: {
      type: Number,
      required: false
    },
    showRemoveButton: {
      type: Boolean,
      default: true
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    emptyMessage: {
      type: String,
      default: 'No finalist comps added yet'
    },
    loadingMessage: {
      type: String,
      default: 'Loading finalist comps...'
    }
  },

  computed: {
    hasComps() {
      return this.compData.length > 0;
    },

    showCriteriaComparison() {
      return this.hasComps && this.ksdCriteria.length > 0;
    },

    gridFields() {
      return [
        {
          field: 'image.view_url',
          type: 'image',
          defaultImage: '/img/placeholder-building.jpg'
        },
        {
          field: 'pivot.score',
          type: 'badge',
          position: 'topRight',
          badge: true,
          classFunction: (item) => {
            const score = item.pivot?.score;
            if (!score) return '';
            if (score >= 80) return 'finalist-grid__card-score-badge finalist-grid__card-score-badge--high';
            if (score >= 60) return 'finalist-grid__card-score-badge finalist-grid__card-score-badge--medium';
            return 'finalist-grid__card-score-badge finalist-grid__card-score-badge--low';
          }
        },
        {
          field: 'address_line_1',
          type: 'title'
        },
        {
          field: 'city',
          type: 'subtitle',
          transform: (value, item) => this.getCompLocation(item)
        },
        {
          field: 'available_square_footage',
          label: 'AVAILABLE SQ. FT.',
          type: 'text',
          labelFirst: true,
          transform: (value) => this.formatCompactNumber(value)
        },
        {
          field: 'total_square_footage',
          label: 'TOTAL SQ. FT.',
          type: 'text',
          labelFirst: true,
          transform: (value) => this.formatCompactNumber(value)
        },
        {
          field: 'avg_rent',
          label: 'AVG RENT PSF',
          type: 'text',
          labelFirst: true,
          transform: (value) => this.formatCurrency(value || 0, 2)
        },
        {
          field: 'parking_ratio',
          label: 'PARKING RATIO X/1K',
          type: 'text',
          labelFirst: true,
          transform: (value) => value || 'N/A'
        }
      ];
    }
  },

  methods: {
    formatCompactNumber(value) {
      if (!value && value !== 0) return 'N/A';
      if (value >= 1000000) {
        return (value / 1000000).toFixed(1) + 'M';
      }
      if (value >= 1000) {
        return (value / 1000).toFixed(1) + 'K';
      }
      return value.toString();
    },

    getCompLocation(comp) {
      const parts = [];
      if (comp.city) parts.push(comp.city);
      if (comp.state) parts.push(comp.state);
      return parts.length > 0 ? parts.join(', ') : 'N/A';
    },

    getCompUrl(comp) {
      return comp.pivot?.view_url || `/sites/${comp.id}`;
    },

    navigateToComp(comp) {
      window.location.href = this.getCompUrl(comp);
    },

    getCompKSDScore(ksd, compPivotId) {
      // Find the score for this comp in the KSD's scores array
      if (!ksd.scores || !Array.isArray(ksd.scores)) return 0;
      const scoreEntry = ksd.scores.find(s => s.scorable_id === compPivotId);
      return scoreEntry?.score || 0;
    },

    getStarLabel(comp, criterion) {
      const score = this.getCompKSDScore(criterion, comp.pivot?.id);
      return `${comp.address_line_1} ${criterion.driver}: ${score} out of 5 stars`;
    },

    async toggleFavorite(comp) {
      if (!comp.pivot || !comp.pivot.id) {
        console.error('Comp pivot data missing');
        return;
      }
      
      const previousState = comp.pivot.is_favorite;
      const favorited_at = previousState ? null : moment().format('Y-MM-D H:m:ss');
      const favorited_by = previousState ? null : window.user.id;
      
      // Optimistic update
      comp.pivot.favorited_at = favorited_at;
      comp.pivot.favorited_by = favorited_by;
      comp.pivot.is_favorite = favorited_at ? true : false;
      
      try {
        if (this.projectId) {
          await axios.patch(`/projects/${this.projectId}/comps/${comp.pivot.id}`, {
            favorited_at: favorited_at,
            favorited_by: favorited_by
          });
        }
        
        this.$emit('favorite-toggled', { comp, is_favorite: comp.pivot.is_favorite });
        
        this.$notify({
          type: 'success',
          title: 'Success',
          text: comp.pivot.is_favorite ? 'Added to favorites' : 'Removed from favorites'
        });
      } catch (err) {
        // Revert on error
        comp.pivot.is_favorite = previousState;
        comp.pivot.favorited_at = previousState ? moment().format('Y-MM-D H:m:ss') : null;
        comp.pivot.favorited_by = previousState ? window.user.id : null;
        console.error('Error toggling favorite:', err);
        this.$notify({
          type: 'error',
          title: 'Error',
          text: err.response?.data?.message || 'Unable to update favorite status'
        });
      }
    },

    removeComp(comp) {
      if (!comp.pivot || !comp.pivot.id) {
        console.error('Comp pivot data missing');
        return;
      }
      
      const compName = comp.address_line_1 || 'this comp';
      
      this.$swal({
        title: 'Remove Comp?',
        text: `Are you sure you want to remove ${compName} from consideration?`,
        type: 'warning',
        buttonsStyling: false,
        confirmButtonText: 'Yes - Remove it',
        confirmButtonClass: 'btn btn-red',
        showCancelButton: true,
        cancelButtonClass: 'btn btn-secondary mr-4',
        reverseButtons: true
      }).then(async (result) => {
        if (result.value) {
          try {
            if (this.projectId) {
              await axios.delete(`/projects/${this.projectId}/comps/${comp.pivot.id}`);
            }
            
            this.$emit('comp-removed', comp);
            
            this.$notify({
              type: 'success',
              title: 'Success',
              text: 'Comp removed from consideration'
            });
          } catch (err) {
            console.error('Error removing comp:', err);
            this.$notify({
              type: 'error',
              title: 'Error',
              text: err.response?.data?.message || 'Unable to remove comp'
            });
          }
        }
      });
    }
  }
};
</script>
